import { type Direction } from '@mui/material/styles';
import { createContext, type ReactNode, useCallback, useMemo, useState } from 'react';
import { merge } from 'ts-deepmerge';
import { reportError } from '@/helpers/errors/report';

export type CreateThemeSettings = {
  direction: Direction;
  responsiveFontSizes: boolean;
};

type AppSettings = {
  dashboardPromps: { newsletter: boolean };
  isWelcomeStepperInProgress?: boolean;
};

export type Settings = CreateThemeSettings & AppSettings;

export const initialSettings: Settings = {
  direction: 'ltr',
  responsiveFontSizes: true,
  dashboardPromps: {
    newsletter: true,
  },
};

export const restoreSettings = (): Settings => {
  let settings = initialSettings;

  try {
    const storedData = window.localStorage.getItem('settings');
    if (storedData) {
      const { dashboardPromps, ...otherSettings } = JSON.parse(storedData) as Settings;
      settings = {
        ...settings,
        dashboardPromps: {
          ...settings.dashboardPromps,
          ...dashboardPromps,
        },
        ...otherSettings,
      };
    }
  } catch (err) {
    reportError(err, { domain: 'settings', action: 'restore-settings', level: 'info' });
  }

  return settings;
};

/**
 * Do not use directly in components, use `const {saveSettings} = useSettings()` instead.
 */
export const storeSettings = (settings: Partial<Settings>) => {
  try {
    window.localStorage.setItem('settings', JSON.stringify(settings));
  } catch (err) {
    reportError(err, { domain: 'settings', action: 'store-settings', level: 'info' });
  }
};

export const SettingsContext = createContext<{ settings: Settings; saveSettings: (value: Partial<Settings>) => void }>({
  settings: initialSettings,
  saveSettings: () => undefined,
});

export const SettingsProvider = ({ children }: { children: (settings: CreateThemeSettings) => ReactNode }) => {
  const [settings, setSettings] = useState(restoreSettings());
  const saveSettings = useCallback(
    (updatedSettings: Partial<Settings>) => {
      setSettings((state) => {
        storeSettings(merge({}, state, updatedSettings));
        return restoreSettings();
      });
    },
    [setSettings],
  );
  const value = useMemo(
    () => ({
      settings,
      saveSettings,
    }),
    [saveSettings, settings],
  );
  return <SettingsContext.Provider value={value}>{children(value.settings)}</SettingsContext.Provider>;
};
